import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAccounts } from "@/lib/useAccounts";
import { useSharedData, invalidate } from "@/lib/useSharedData";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { ExportButtons } from "@/components/ExportButtons";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { TxRow } from "@/components/transactions/TxRow";
import { TxEditModal } from "@/components/transactions/TxEditModal";
import { TxStatsChips } from "@/components/transactions/TxStatsChips";
import { TxTotalCounter } from "@/components/transactions/TxTotalCounter";

const _EMPTY = "";

export default function Transactions() {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ start: "", end: "" });
  const [pending, setPending] = useState(null);
  const [editing, setEditing] = useState(null);
  const { items: accounts = [] } = useAccounts();
  const { data: catsData } = useSharedData("categories", "/categories", 5 * 60_000);
  const cats = catsData || [];
  const filtersKey = `${q}|${type}|${category}|${range.start}|${range.end}`;
  const isBaseView = !q && !type && !category && !range.start && !range.end;
  const { data: baseRows } = useSharedData(isBaseView ? "tx-base" : _EMPTY, "/transactions?limit=500");

  const load = () => {
    if (isBaseView) { invalidate("tx-base"); }
    const params = new URLSearchParams();
    if (q) params.set("q", q); if (type) params.set("type", type);
    if (category) params.set("category", category);
    if (range.start) params.set("start", range.start);
    if (range.end) params.set("end", range.end);
    params.set("limit", "500");
    api.get(`/transactions?${params}`).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => { if (isBaseView && baseRows) setRows(baseRows); }, [baseRows, isBaseView]);
  useEffect(() => {
    const tm = setTimeout(() => load(), 200);
    const onChange = () => { invalidate("tx-base"); load(); };
    window.addEventListener("malat:data-changed", onChange);
    return () => { clearTimeout(tm); window.removeEventListener("malat:data-changed", onChange); };
  }, [filtersKey]); // eslint-disable-line

  const catMap = Object.fromEntries(cats.map((c) => [c.id, c]));
  const acctMap = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const doDelete = async () => {
    if (!pending) return;
    try { await api.delete(`/transactions/${pending.id}`); toast.success(t("saved")); invalidate("tx-base"); load(); }
    catch { toast.error(t("error")); }
    finally { setPending(null); }
  };

  return (
    <main className="neu-scope max-w-4xl mx-auto px-4 sm:px-6 pt-[calc(env(safe-area-inset-top)+5rem)] pb-32 space-y-4 overflow-x-hidden">
      <TxTotalCounter />
      <div className="p-4 rounded-3xl bg-card border border-border space-y-2 w-full">
        <div className="relative w-full">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input data-testid="tx-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("search")}
            className="w-full ps-10 pe-4 py-3 rounded-2xl border border-border bg-card focus:border-primary focus:outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-2 w-full">
          <select value={type} onChange={(e) => setType(e.target.value)} className="w-full px-3 py-3 rounded-2xl border border-border bg-card">
            <option value="">{t("type")}</option>
            {["expense","income","revenue","savings","borrow","lend","recurring"].map((k) => <option key={k} value={k}>{t(k)}</option>)}
          </select>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-3 rounded-2xl border border-border bg-card">
            <option value="">{t("category")}</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c[lang] || c.en}</option>)}
          </select>
        </div>
        <ExportButtons filters={{ type, ...range }} />
      </div>
      <div className="px-1"><DateRangeFilter onChange={setRange} /></div>
      <TxStatsChips activeType={type} onPick={setType} />
      <div className="grid gap-2 w-full">
        {rows.length === 0 && <div className="p-6 rounded-3xl border border-dashed border-border text-center text-sm text-muted-foreground">{t("notFound")}</div>}
        {rows.map((tx) => (
          <TxRow key={tx.id} tx={tx} cat={catMap[tx.category]} lang={lang} acctMap={acctMap} onDelete={() => setPending(tx)} onEdit={() => setEditing(tx)} />
        ))}
      </div>
      <TxEditModal open={!!editing} tx={editing} cats={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <ConfirmDelete open={!!pending} onCancel={() => setPending(null)} onConfirm={doDelete}
        title={lang === "ar" ? "حذف المعاملة؟" : "Delete transaction?"}
        message={pending ? `${pending.description || pending.category} · ${pending.final_currency} ${Number(pending.final_amount).toLocaleString()}` : ""} />
    </main>
  );
}
